import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  CircularProgress,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  HelpCircle as HelpIcon,
  ArrowLeft,
  Download,
  Trash2
} from 'lucide-react';

import BudgetService from '../services/AGTUps-product-services/budgetService';
import { toast } from 'react-toastify';

// EJEMPLOS de componentes o estilos que replican tu estilo de shortcuts:
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    },
    typography: {
      h6: {
        fontWeight: 600
      },
      button: {
        textTransform: 'none'
      }
    }
  });

/** 
 * Tarjeta sencilla para mostrar cada Budget. 
 * Usa un estilo minimalista. 
 */
const BudgetCard = ({ budget, onClick }) => {
  return (
    <Paper
      onClick={() => onClick(budget)}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: '12px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {budget.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {budget.description || 'No description'}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Total: ${Number(budget.totalBudget || 0).toLocaleString()}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {budget.categories?.length || 0} categories
      </Typography>
    </Paper>
  );
};

/** 
 * Componente principal del Shortcut: ViewBudgetsShortcut 
 * 
 * - `open`: controla si el modal está abierto
 * - `onClose`: función para cerrarlo
 */
const ViewBudgetsShortcut = ({ open, onClose }) => {
  // Usamos el theme "padre" para determinar el modo.
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [budgets, setBudgets] = useState([]);

  // Para ver detalle de un Budget dentro del modal
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (open) {
      fetchBudgets();
    } else {
      // Resetear estados si deseas
      setSelectedBudget(null);
    }
    // eslint-disable-next-line
  }, [open]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await BudgetService.getBudgets();
      if (response.success) {
        setBudgets(response.data);
      } else {
        throw new Error('Error loading budgets');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Manejo de snackbar
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Eliminar un presupuesto
  const handleDeleteBudget = async (budgetId) => {
    if (!window.confirm('Are you sure you want to delete this budget?')) return;
    try {
      await BudgetService.deleteBudget(budgetId);
      setBudgets((prev) => prev.filter((b) => b.budgetId !== budgetId));
      showSnackbar('Budget deleted successfully', 'info');
      setSelectedBudget(null);
    } catch (error) {
      console.error('Error deleting budget:', error);
      showSnackbar('Failed to delete budget', 'error');
    }
  };

  // Cerrar la vista de detalle
  const handleCloseDetail = () => {
    setSelectedBudget(null);
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="view-budgets-dialog-title"
      >
        <DialogTitle
          id="view-budgets-dialog-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            View Budgets
          </Typography>
          <IconButton size="small">
            <HelpIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: 360 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {!selectedBudget ? (
                // ============ LISTADO DE BUDGETS ============
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Choose a budget to see details
                  </Typography>
                  <Grid container spacing={2}>
                    {budgets.map((budget) => (
                      <Grid item xs={12} sm={6} md={4} key={budget.budgetId}>
                        <BudgetCard
                          budget={budget}
                          onClick={(b) => setSelectedBudget(b)}
                        />
                      </Grid>
                    ))}
                    {budgets.length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          No budgets found.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ) : (
                // ============ VISTA DE DETALLE DE UN BUDGET ============
                <Box>
                  <Button
                    onClick={handleCloseDetail}
                    startIcon={<ArrowLeft size={16} />}
                    sx={{ mb: 2, textTransform: 'none' }}
                  >
                    Back
                  </Button>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedBudget.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    {selectedBudget.description}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    Total Budget: $
                    {Number(selectedBudget.totalBudget).toLocaleString()}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Categories ({selectedBudget.categories?.length || 0})
                  </Typography>
                  {selectedBudget.categories?.length ? (
                    selectedBudget.categories.map((cat) => (
                      <Box key={cat.categoryId} sx={{ mb: 1, pl: 2 }}>
                        - {cat.name} (${cat.budget} planned, ${cat.spent} spent)
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ ml: 2, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No categories yet.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download size={16} />}
                      onClick={() => toast.info('Export not implemented')}
                    >
                      Export
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Trash2 size={16} />}
                      onClick={() => handleDeleteBudget(selectedBudget.budgetId)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ borderRadius: '8px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Dialog>
    </ThemeProvider>
  );
};

export default ViewBudgetsShortcut;
